import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { ThemeService } from '../../core/services/theme.service';
import { AuthService } from '../../core/services/auth.service';
import { TopbarSearchComponent } from './topbar-search';

/**
 * Desktop / tablet top-bar (>= 768px). DateFirma design-system chrome:
 * logo + horizontal nav + ⌘K quick-search + theme toggle + auth CTA.
 */
@Component({
  selector: 'app-topbar',
  imports: [RouterLink, RouterLinkActive, TopbarSearchComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <header class="topbar">
      <a routerLink="/" class="df-logo" aria-label="DateFirma — Acasă">
        <img src="/logo.webp" class="mark" alt="DateFirma.ro - logo director de firme din România" title="DateFirma.ro" width="30" height="30" />
        <span class="word">Date<strong>Firma</strong><span class="dot">.ro</span></span>
      </a>

      <nav aria-label="Navigare principală">
        <a routerLink="/cauta-firme" routerLinkActive="active">Caută firme</a>
        <a routerLink="/top-firme" routerLinkActive="active">Top firme</a>
        <a routerLink="/firme-nou-infiintate" routerLinkActive="active">Firme noi</a>
        <a routerLink="/harta-firme" routerLinkActive="active">Hartă</a>
        <a routerLink="/nomenclatoare" routerLinkActive="active">Nomenclatoare</a>
        <a routerLink="/comparatie" routerLinkActive="active">Comparație</a>
        @if (authService.isLoggedIn()) {
          <a routerLink="/export-firme" routerLinkActive="active">Export</a>
        }
      </nav>

      <app-topbar-search />

      <div class="right">
        <button
          type="button"
          class="theme-btn"
          (click)="themeService.toggle()"
          [attr.aria-label]="themeService.isDark() ? 'Comută la modul luminos' : 'Comută la modul întunecat'"
          title="Schimbă tema">
          @if (themeService.isDark()) {
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 13A9 9 0 1 1 11 3a7 7 0 0 0 10 10z"/></svg>
          } @else {
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>
          }
        </button>

        @if (authService.currentUser(); as user) {
          <a routerLink="/profil" class="df-btn sm">
            <span class="avatar">{{ user.firstName.charAt(0) }}</span>
            {{ user.firstName }}
          </a>
          <button type="button" class="df-btn ghost sm" (click)="authService.logout()">Ieșire</button>
        } @else {
          <a [routerLink]="['/autentificare']" [queryParams]="{ returnUrl: router.url }" class="df-btn primary sm">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"/></svg>
            Autentificare
          </a>
        }
      </div>
    </header>
  `,
  styles: [`
    :host { display: none; position: sticky; top: 0; z-index: 50; }
    @media (min-width: 768px) { :host { display: block; } }
    .topbar {
      display: flex; align-items: center; gap: 18px; height: 64px; padding: 0 28px;
      border-bottom: 1px solid var(--border); background: var(--bg-elev);
    }
    nav { display: flex; gap: 4px; flex-shrink: 0; }
    nav a {
      padding: 8px 12px; border-radius: 9px; font-size: 13.5px; font-weight: 500;
      color: var(--text-muted); white-space: nowrap; text-decoration: none;
    }
    nav a:hover { background: var(--surface-2); color: var(--text); }
    nav a.active { background: var(--accent-tint); color: var(--accent); font-weight: 600; }
    app-topbar-search { flex: 1; display: flex; justify-content: flex-end; margin-left: auto; min-width: 0; }
    .right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
    .theme-btn {
      width: 38px; height: 38px; border-radius: 10px; background: transparent;
      border: 1px solid var(--border); color: var(--text-muted);
      display: flex; align-items: center; justify-content: center; cursor: pointer;
    }
    .theme-btn:hover { background: var(--surface-2); color: var(--text); }
    .avatar {
      width: 22px; height: 22px; border-radius: 50%; background: var(--accent); color: #fff;
      display: inline-flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700;
    }
    a.df-btn { text-decoration: none; }
  `],
})
export class TopbarComponent {
  protected readonly router = inject(Router);
  protected readonly themeService = inject(ThemeService);
  protected readonly authService = inject(AuthService);
}
